import type { AasApiAdapter } from "../adapter";
import { requestJson } from "../http";
import type { UsageOverview } from "../types";
import { mapApiKey } from "./apiKeys";
import type { BackendApiKey, BackendUsage } from "./backendTypes";
import { usageQueryParams } from "./shared";

function toBucketLabel(bucket: string): string {
	const date = new Date(bucket);
	if (Number.isNaN(date.getTime())) {
		return bucket;
	}
	const iso = date.toISOString();
	// Hour buckets keep their hour so labels stay unique; day buckets stay date-only.
	return iso.slice(11, 16) === "00:00" ? iso.slice(0, 10) : `${iso.slice(0, 13)}:00`;
}

function toUsageOverview(usage: BackendUsage): UsageOverview {
	return {
		summary: {
			totalTokens: usage.summary.totalTokens,
			inputTokens: usage.summary.inputTokens,
			outputTokens: usage.summary.outputTokens,
			totalCostUsd: usage.summary.costUsd,
			conversations: usage.summary.conversations,
			turns: usage.summary.turns,
		},
		series: usage.buckets.map((bucket) => ({
			label: toBucketLabel(bucket.bucket),
			tokens: bucket.totalTokens,
			costUsd: bucket.costUsd,
		})),
	};
}

export const usageApi: AasApiAdapter["usage"] = {
	async overview(query) {
		if (query.profile) {
			return this.byProfile(query.profile, query);
		}
		if (query.apiKeyId) {
			const {
				keyName: _keyName,
				budget: _budget,
				...scoped
			} = await this.byApiKey(query.apiKeyId, query);
			return scoped;
		}

		const usage = await requestJson<BackendUsage>("/api/usage", {
			query: usageQueryParams(query),
		});
		return toUsageOverview(usage);
	},
	async byProfile(slug: string, query) {
		const usage = await requestJson<BackendUsage>(
			`/api/usage/profiles/${encodeURIComponent(slug)}`,
			{
				query: usageQueryParams(query),
			},
		);
		return toUsageOverview(usage);
	},
	async byApiKey(id: string, query) {
		const usage = await requestJson<BackendUsage>(`/api/usage/api-keys/${encodeURIComponent(id)}`, {
			query: usageQueryParams(query),
		});
		const apiKeys = await requestJson<BackendApiKey[]>("/api/api-keys").catch(() => []);
		const key = apiKeys.find((entry) => entry.id === id);
		const mapped = key ? mapApiKey(key) : undefined;
		const budgetLimit = mapped?.rateLimits.monthlyTokenBudget;

		const overview = { ...toUsageOverview(usage), keyName: mapped?.name };
		if (!budgetLimit) return overview;

		// The runtime counts the budget in tokens over the current UTC calendar
		// month, whatever range the page is showing.
		const today = new Date().toISOString().slice(0, 10);
		const month = await requestJson<BackendUsage>(`/api/usage/api-keys/${encodeURIComponent(id)}`, {
			query: usageQueryParams({
				from: `${today.slice(0, 8)}01`,
				to: today,
				granularity: "total",
			}),
		});
		return { ...overview, budget: { used: month.summary.totalTokens, limit: budgetLimit } };
	},
};
